import { fromUnixTime } from 'date-fns';
import { computed, observable, action } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    displayFormatTime,
    DisplayMapMarker,
    getTimezone,
    getWeatherIcon,
    humanizeString,
    IconCard,
    IconCardItem,
    inject,
    ItemCarousel,
    Loader,
    ModalHandle,
    Popout,
    PopoutHandle,
    scaleLevel,
    Tooltip,
    toTimezoneAndFormat,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowRightShort } from '@react-icons/all-files/bs/BsArrowRightShort';
import { BsBarChart } from '@react-icons/all-files/bs/BsBarChart';
import { BsFillClockFill } from '@react-icons/all-files/bs/BsFillClockFill';
import { BsMoon } from '@react-icons/all-files/bs/BsMoon';
import { BsMap } from '@react-icons/all-files/bs/BsMap';
import { BsSun } from '@react-icons/all-files/bs/BsSun';
import { LinkContainer } from 'react-router-bootstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { Labels } from '../BI/BiStore';
import { ContractsStore } from '../Contracts/ContractsStore';
import { AppStore } from '../Core/AppStore';
import { TicketModal } from '../Core/TicketModal';
import { WeatherStore } from '../Weather/WeatherStore';
import { ActiveDisplay } from './ActiveDisplay';
import { DisplayScreenSnapshot } from './DisplayScreenSnapshot';
import { DisplaysStore } from './DisplaysStore';

export interface IDisplayDetailsPopoutProps {
    onClosed?(): void;

    readonly display: ActiveDisplay;
    readonly popoutHandle: PopoutHandle;
    readonly statusPoll?: Mri.DisplayStatuses.DisplayStatusPoll;
}

@autoinject
@observer
export class DisplayDetailsPopout extends React.Component<IDisplayDetailsPopoutProps> {
    public render() {
        const { display, popoutHandle } = this.props;
        const onTicketCountClick = !display.openTicketCount ? this.openCreateTicketModal : this.openTicketsModal;
        const weatherVuStatus = this.props.statusPoll?.weatherVuStatus;

        return (
            <Popout popoutHandle={popoutHandle} onOpened={this.loadAsync} size="side">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12 flex-items-center">
                            <h4 className="d-inline-block mb-0">{display.serialNumber}</h4>

                            <LinkContainer to={`/displays/${display.serialNumber}`}>
                                <a className="ms-auto btn btn-link">
                                    Go To Display <BsArrowRightShort style={{ fontSize: 25 }} />
                                </a>
                            </LinkContainer>
                        </div>
                        {!this.loading && (
                            <>
                                <div className="col-12 mb-2">
                                    {this.screenSnapshots && (
                                        <ItemCarousel
                                            className="rounded"
                                            items={this.screenSnapshots}
                                            renderItem={i => <DisplayScreenSnapshot screenSnapshot={i} />}
                                        />
                                    )}
                                </div>
                                <div className="col-12 mb-2">
                                    <IconCard icon={<BsBarChart />} bgColor="blue">
                                        <strong>Status: {display.statusMessageBrief}</strong>
                                        <div>
                                            <IconCardItem
                                                title="Tickets"
                                                data={
                                                    <a className="white-link" onClick={onTicketCountClick}>
                                                        {display.openTicketCount}
                                                    </a>
                                                }
                                            />

                                            <IconCardItem title="Events" data={display.newDisplayEventCount} />
                                            <IconCardItem
                                                title="Video"
                                                data={
                                                    <span
                                                        className={
                                                            'font-weight-bold text-' +
                                                            this.labelCardColor(this.videoStatus)
                                                        }
                                                    >
                                                        {this.videoStatus}
                                                    </span>
                                                }
                                            />
                                        </div>
                                    </IconCard>
                                </div>

                                <div className="col-12 mb-2">
                                    <IconCard icon={<BsMap />} bgColor="green">
                                        <div>
                                            <strong>{this.unitLocation}</strong>
                                            <div>{display.unitLocation?.address1}</div>
                                            <div>{display.unitLocation?.country}</div>
                                            <div>{display.unitLocation?.description}</div>
                                        </div>
                                    </IconCard>
                                </div>

                                <div className="col-12 mb-2">
                                    <IconCard
                                        icon="none"
                                        bgColor="purple"
                                        customIcon={<img src={this.weatherIcon} width={50} height={50} />}
                                    >
                                        <div>
                                            <strong>
                                                {!!this.displayWeather?.weather?.length
                                                    ? humanizeString(this.displayWeather?.weather[0].description)
                                                    : 'Invalid Weather Location'}
                                            </strong>

                                            {!!this.displayWeather?.weather && (
                                                <div>
                                                    {this.displayWeather.main &&
                                                        !this.accountStore!.user!.fahrenheitPreferred && (
                                                        <h4 className="mb-0">
                                                            <Tooltip message="Current Temperature">
                                                                {(this.displayWeather.main.temp - 273.15).toFixed(
                                                                    1
                                                                )}
                                                                    &#8451;
                                                            </Tooltip>
                                                        </h4>
                                                    )}
                                                    {this.accountStore!.user!.fahrenheitPreferred && (
                                                        <h4 className="mb-0">
                                                            <Tooltip message="Current Temperature">
                                                                {(
                                                                    (this.displayWeather.main.temp - 273.15) * 1.8 +
                                                                    32
                                                                ).toFixed(1)}
                                                                &#8457;
                                                            </Tooltip>
                                                        </h4>
                                                    )}
                                                </div>
                                            )}

                                            <div>
                                                {this.displayWeather?.main &&
                                                    !this.accountStore!.user!.fahrenheitPreferred && (
                                                    <Tooltip message="High/Low">
                                                        {(this.displayWeather.main.temp_min - 273.15).toFixed(1)}
                                                            &#8451; /{' '}
                                                        {(this.displayWeather.main.temp_max - 273.15).toFixed(1)}
                                                            &#8451;
                                                    </Tooltip>
                                                )}

                                                {this.displayWeather?.main &&
                                                    this.accountStore!.user!.fahrenheitPreferred && (
                                                    <Tooltip message="High/Low">
                                                        {(
                                                            (this.displayWeather.main.temp_min - 273.15) * 1.8 +
                                                                32
                                                        ).toFixed(1)}
                                                            &#8457; /{' '}
                                                        {(
                                                            (this.displayWeather.main.temp_max - 273.15) * 1.8 +
                                                                32
                                                        ).toFixed(1)}
                                                            &#8457;
                                                    </Tooltip>
                                                )}
                                            </div>

                                            <IconCardItem
                                                title="Local Humidity"
                                                data={
                                                    weatherVuStatus?.localHumidity != null
                                                        ? scaleLevel(weatherVuStatus.localHumidity) + '%'
                                                        : '-'
                                                }
                                            />
                                        </div>
                                    </IconCard>
                                </div>

                                <div className="col-12 mb-2">
                                    <IconCard icon={<BsFillClockFill />} bgColor="pink">
                                        <strong>
                                            {this.props.display.pollTime
                                                ? displayFormatTime(
                                                    this.props.display.pollTime,
                                                    'h:mm a',
                                                    this.props.display.serialNumber
                                                )
                                                : 'No Time Reported'}
                                        </strong>
                                        <div>{getTimezone(this.props.display.serialNumber)}</div>

                                        <div>
                                            {this.props.display.pollTime
                                                ? displayFormatTime(
                                                    this.props.display.pollTime,
                                                    'MMM d, yyyy',
                                                    this.props.display.serialNumber
                                                )
                                                : 'No Time Reported'}
                                        </div>

                                        {this.sunrise && this.sunset && (
                                            <div>
                                                <div className="d-inline-block pe-2">
                                                    <BsSun />
                                                    &nbsp;
                                                    {toTimezoneAndFormat(this.sunrise, 'h:mm a', 'UTC')}
                                                </div>
                                                -
                                                <div className="d-inline-block ps-2">
                                                    <BsMoon /> &nbsp;
                                                    {toTimezoneAndFormat(this.sunset, 'h:mm a', 'UTC')}
                                                </div>
                                            </div>
                                        )}
                                    </IconCard>
                                </div>
                            </>
                        )}

                        <div className="col-12 text-center">
                            <Loader loading={this.loading} message="Loading Data..." showSpinner />
                        </div>
                    </div>

                    {this.renderModals && (
                        <>
                            <TicketModal display={display} modalHandle={this.createTicketModalHandle} />
                        </>
                    )}
                </div>
            </Popout>
        );
    }

    @bind
    @action
    private async loadAsync() {
        this.loading = true;

        const { serialNumber } = this.props.display;

        try {
            await this.displaysStore.loadAsync(serialNumber, {
                params: {
                    status: true,
                    contract: true,
                },
            });

            if (!this.props.display) throw new Error(`${serialNumber} does not exist`);

            const promises: Promise<any>[] = [];

            if (this.props.display!.contract)
                promises.push(this.contractsStore.loadAsync(this.props.display!.contract, true).catch(console.error));

            promises.push(this.displaysStore.loadCurrentStatusAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.loadOpenTicketsAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.loadLatestScreenSnapshotAsync(serialNumber).catch(console.error));
            promises.push(this.weatherStore.loadWeatherAsync(serialNumber).catch(console.error));
            promises.push(this.displaysStore.refreshScreenSnapshotAsync(serialNumber).catch(console.error));

            await Promise.all(promises);
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @computed
    private get screenSnapshots() {
        return this.displaysStore.screenSnapshots.get(this.props.display.serialNumber);
    }

    @bind
    @action
    private openCreateTicketModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.createTicketModalHandle.open();
    }

    @bind
    @action
    private openTicketsModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.ticketsModalHandle.open();
    }

    @computed
    private get weatherIcon() {
        const icon = (this.displayWeather?.weather && this.displayWeather.weather[0]?.icon) ?? '';
        return getWeatherIcon(icon);
    }

    @computed
    private get displayWeather() {
        return this.weatherStore.weathers.get(this.props.display.serialNumber);
    }

    @computed
    private get sunrise() {
        return this.displayWeather?.sys?.sunrise
            ? fromUnixTime(this.displayWeather.sys.sunrise + this.displayWeather.timezone)
            : null;
    }

    @computed
    private get sunset() {
        return this.displayWeather?.sys?.sunset
            ? fromUnixTime(this.displayWeather.sys.sunset + this.displayWeather.timezone)
            : null;
    }

    @computed
    private get account() {
        return this.accountsStore.accounts.get(this.props.display.account);
    }

    @computed
    private get unitLocation() {
        const display = this.props.display;

        if (!display || !display.unitLocation) return 'Invalid Location';

        const addr: string[] = [];

        if (display.unitLocation.latitude && display.unitLocation.longitude && this.displayWeather) {
            addr.push(this.displayWeather.name);
        } else if (display.unitLocation) {
            addr.push(display.unitLocation.city);
            addr.push(display.unitLocation.stateOrProvince);
        }

        return addr.filter(a => a).join('\n') || 'Invalid Location';
    }

    @computed
    private get videoStatus() {
        switch (this.props.display.videoState) {
            case 'noVideo':
                return Labels.NotPlaying;
            case 'showingVideo':
                return Labels.Playing;
            case 'warning':
                return Labels.Warning;
        }
    }

    private labelCardColor(label: string) {
        switch (label) {
            default:
            case Labels.Offline:
                return 'gray';
            case Labels.Good:
                return 'white';
            case Labels.Critical:
                return 'danger';
            case Labels.Warning:
                return 'warning';
            case Labels.Playing:
                return 'white';
            case Labels.NotPlaying:
                return 'danger';
        }
    }

    @observable
    private renderModals = false;
    @observable
    private loading = false;
    private createTicketModalHandle = new ModalHandle();
    private ticketsModalHandle = new ModalHandle();
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private weatherStore!: WeatherStore;
    @inject
    private contractsStore!: ContractsStore;
    @inject
    private appStore!: AppStore;
}
